import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibContainer } from '@chewy/kib-layout-react'
import { Tooltip } from '@mui/material'
import classNames from 'classnames'
import { PermissionArea } from '@pbt/pbt-ui-components'

import { getCRUDByArea } from '~/store/reducers/auth'
import { getMemberIsLoading } from '~/store/reducers/members'

import { PrescriptionActionExpandedContext } from '../../PrescriptionContext'
import PrescriptionDeclineActionChirp from './PrescriptionDeclineActionChirp'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-layout-styles/dist/styles/kib-container.css'
import styles from './prescriptionChewyReactiveRxActionsChirp.module.scss'

export interface PrescriptionChewyReactiveRxActionsClasses {
  button?: string
  declineButton?: string
  saveButton?: string
}

export interface PrescriptionChewyReactiveRxActionsProps {
  classes?: PrescriptionChewyReactiveRxActionsClasses
}

export const PrescriptionChewyReactiveRxActions = ({
  classes: classesProp,
}: PrescriptionChewyReactiveRxActionsProps) => {
  const { t } = useTranslation(['Common', 'Tooltips'])

  const permissions = useSelector(getCRUDByArea(PermissionArea.PRESCRIPTION))
  const memberIsLoading = useSelector(getMemberIsLoading)
  const {
    disableDrafts,
    automaticallyCreateTaskField,
    canApproveAndSubmitChewyRx,
    isChewyCatalogItemNotLinked,
    isDvmLicenseRequired,
    isLoadingOrUpdating,
    isSending,
    needsDvmLicenseEntry,
    needsDvmLicenseEntryTooltip,
    needsLicenseEntry,
    needsStateLicenseEntryTooltip,
    onApprove,
    onDecline,
    onSaveDraft,
    prescriptionForTracking,
  } = useContext(PrescriptionActionExpandedContext)

  const isLoading = isSending || (memberIsLoading && isDvmLicenseRequired)

  return (
    <KibContainer className={styles.container}>
      <Tooltip
        placement="top"
        title={
          (isChewyCatalogItemNotLinked
            ? t('Tooltips:PRESCRIPTION_MATCHING_ACTION_TOOLTIP')
            : null) ||
          needsDvmLicenseEntryTooltip ||
          needsStateLicenseEntryTooltip
        }
      >
        <span>
          <KibButtonNew
            className={classesProp?.button}
            disabled={
              !canApproveAndSubmitChewyRx ||
              isLoadingOrUpdating ||
              needsLicenseEntry ||
              isChewyCatalogItemNotLinked
            }
            key="reactiveRxApprove"
            loading={isLoading}
            size="small"
            theme="action"
            onClick={onApprove}
          >
            {t('Common:APPROVE_ACTION')}
          </KibButtonNew>
        </span>
      </Tooltip>

      {onDecline && (
        <PrescriptionDeclineActionChirp
          className={classesProp?.declineButton}
          disabled={
            !permissions.update || isLoadingOrUpdating || needsLicenseEntry
          }
          loading={isLoading}
          prescriptionForTracking={prescriptionForTracking}
          onDecline={onDecline}
        />
      )}
      <Tooltip
        title={
          disableDrafts && automaticallyCreateTaskField.value
            ? t('Tooltips:PRESCRIPTION_DISABLED_DRAFTS_TOOLTIP')
            : ''
        }
      >
        <span>
          <KibButtonNew
            className={classNames(classesProp?.button, classesProp?.saveButton)}
            disabled={
              !permissions.update ||
              (disableDrafts && automaticallyCreateTaskField.value) ||
              isLoadingOrUpdating
            }
            emphasis="secondary"
            key="reactiveRxSaveDraft"
            loading={isSending}
            size="small"
            theme="action"
            onClick={onSaveDraft}
          >
            {t('Common:SAVE_DRAFT_ACTION')}
          </KibButtonNew>
        </span>
      </Tooltip>
    </KibContainer>
  )
}
